import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import useIsMobile from "../hooks/useIsMobile";
import { useToday } from "../hooks/useToday";
import ReplyPanel from "./ReplyPanel";
import SwipeAct from "./SwipeAct";
import ContactDetailPage from "./ContactDetailPage";
import { BOARD_STAGE_LABELS, sourcePill } from "./ui";
import { meetingPurpose } from "./meetingPurpose.mjs";
import { CALL_ACTIONS, byCall, tickPath, tickRequest } from "./callActions.mjs";
import { shortDate } from "./record/format.mjs";
import {
  NOT_QUEUE, SECTION_HINTS, SECTION_SHORT, doneText, waitLabel, localMidnight, hhmm,
  bucketCalls, stripRange, hourOf, laneCalls, callWho, nextCall, untilLabel, greeting,
  progressLine, pipelineRows, feedGroups, feedTime, MARK_GLYPHS,
  hotIds, arrangeQueue, feedByLead, feedLeadLine, feedLine,
  owed, answeredByCall, callTodayLine, busyFor, bucketBusy, laneBusy, replyIds,
} from "./todayModel.mjs";
import "./today.css";

// THE MORNING PAGE, redesigned. design/today/Today.dc.html is the source of truth
// (Gabriel's mockup of 2026-09-30); this file draws it from live data.
//
// Four questions, top to bottom: how is the day shaped (the hero and the day
// strip), what needs me (the queue), where does the pipeline stand, and what
// happened this week. The queue is still exactly the action layer's — the page
// computes nothing about what is due, so it cannot disagree with the board's chips.
// The rest comes off GET /today?brief=1 (src/actions/briefing.js).
//
// Deliberate departures from the mockup, each because the thing drawn does not
// exist yet: no "Send at 09:00" (there is no scheduled send), no Undo on a sent
// email (it has gone), no in-place "approve all" (the tile opens Approvals, where
// each change is read before it is applied), and "Send invite" reads "Check": the
// CRM cannot send a calendar invite, it can show you the record.

// Which actions are answered by writing an email. These get the draft flow; the
// rest open the record.
const REPLYABLE = new Set([
  "reply_waiting", "agreement_reply", "nudge_due", "final_check_due", "pricing_stale", "agreement_stale",
  "call_followup", "first_touch", "kickoff", "finance_intro",
]);

// A reply they are waiting on is the one "waiting on you" mark in the queue: the
// pulsing red dot, per the board's rule in design-notes.md.
const WAITING = new Set(["reply_waiting", "agreement_reply"]);

const rowKey = (a) => `${a.kind}:${a.company_id}`;

// Honour the OS setting: the count-up and the bars growing are decoration.
const REDUCED = typeof window !== "undefined" && window.matchMedia
  && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** 0 → 1 over ~900ms once, for the count-up and the pipeline bars. */
function useIntro() {
  const [t, setT] = useState(REDUCED ? 1 : 0);
  useEffect(() => {
    if (REDUCED || document.hidden) { setT(1); return undefined; }
    let raf;
    const s = performance.now();
    const step = (n) => {
      const x = Math.min(1, (n - s) / 900);
      setT(1 - Math.pow(1 - x, 3));
      if (x < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);
  return t;
}

/** A clock that ticks each minute, so "now" and "next in" do not freeze at load. */
function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);
  return now;
}

function SourcePill({ source }) {
  const pill = sourcePill(source);
  if (!pill) return null;
  return <span className={`crm-pill ${pill.cls} crm-td__src`}>{pill.label}</span>;
}

/* ----------------------------------------------------------------- hero */

function Hero({ now, remaining, callsToday, invites, done, total, first, t, onOpen, onRefresh }) {
  const date = now.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
  const cells = Array.from({ length: total }, (_, i) => i < done);
  const inv = invites[0];
  return (
    <div className="crm-td__hero">
      <div className="crm-td__hero-main">
        <div className="crm-td__date">
          {date} · {hhmm(now)}
          <button type="button" className="crm-td__refresh" onClick={onRefresh} aria-label="Refresh" title="Refresh">↻</button>
        </div>
        <div className="crm-td__h1">{greeting(now)}, Gabriel.</div>
        <div className="crm-td__sent">
          <span className="crm-td__big">{Math.round(remaining * t)}</span>
          <span>{remaining === 1 ? "thing needs you" : "things need you"},</span>
          {callsToday ? (
            <>
              <b>{Math.round(callsToday * t)}</b>
              <span>{callsToday === 1 ? "call today." : "calls today."}</span>
            </>
          ) : (
            <span>no calls today.</span>
          )}
        </div>
        {inv && (
          <div className="crm-td__alert">
            <span>
              <span className="crm-td__alert-dot" aria-hidden="true" />
              <b>{inv.company_name}</b> is at Call scheduled with {inv.detail === "they declined the invite" ? "a declined invite" : "nothing on the calendar"}.
              {invites.length > 1 ? ` ${invites.length - 1} more like it.` : " It won’t happen as is."}
            </span>
            <button type="button" className="crm-td__alert-act" onClick={() => onOpen(inv)}>Check ↗</button>
          </div>
        )}
      </div>
      {total > 0 && (
        <div className="crm-card crm-td__prog">
          <div className="crm-td__prog-n">
            <span className="crm-td__prog-big">{done}</span>
            <span>of {total} done today</span>
          </div>
          <div className="crm-td__cells">
            {cells.map((on, i) => <div key={i} className={`crm-td__cell${on ? " is-on" : ""}`} />)}
          </div>
          <div className="crm-td__prog-line">{progressLine({ done, remaining, first })}</div>
        </div>
      )}
    </div>
  );
}

function AllClear({ now, calls, win }) {
  const date = now.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
  const { today, tomorrow } = bucketCalls(calls, now);
  const next = nextCall(today, now) || tomorrow[0];
  const line = next
    ? `Nothing needs you right now. Next up is ${today.includes(next) ? "today" : "tomorrow"} at ${hhmm(next.at)} with ${next.company_name}.`
    : "Nothing needs you right now, and there are no calls today or tomorrow.";
  const w = win ? feedLine(win) : null;
  return (
    <div className="crm-td__clear">
      <div className="crm-td__clear-main">
        <div className="crm-td__date">{date} · {hhmm(now)}</div>
        <div className="crm-td__clear-h">All clear, Gabriel.</div>
        <div className="crm-td__clear-p">{line}</div>
      </div>
      {w && (
        <div className="crm-td__clear-win">
          <div className="crm-td__clear-win-k">{feedGroups([win], now)[0].label}</div>
          <div className="crm-td__clear-win-t">Won {w.title}</div>
          <div className="crm-td__clear-win-s">{w.sub}</div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- my day */

const CARD_W = 264;

const BUSY_H = 26;

function DayStrip({ calls, busy, isToday, now, next, range, onOpen }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(900);
  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width || 900));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  const hours = range.end - range.start;
  const pos = (h) => `${(((h - range.start) / hours) * 100).toFixed(2)}%`;
  // A card is a fixed width, so how many hours it covers depends on the strip.
  const span = ((CARD_W + 12) / Math.max(width, 1)) * hours;
  const laned = laneCalls(calls, span);
  const lanes = Math.max(1, ...laned.map((l) => l.lane + 1));
  // The rest of his calendar, as grey bars above the call cards (busyFor).
  const busyLaned = laneBusy(busy);
  const busyLanes = busyLaned.length ? Math.max(...busyLaned.map((l) => l.lane + 1)) : 0;
  const top = 24 + busyLanes * (BUSY_H + 4) + (busyLanes ? 6 : 0);
  const nowH = hourOf(now);
  // A block that started yesterday or runs into tomorrow is clipped to the strip.
  const clip = (ts, edge) => {
    const d = new Date(ts);
    const h = hourOf(d) + (d.getDate() !== edge.getDate() ? (d < edge ? -24 : 24) : 0);
    return Math.min(range.end, Math.max(range.start, h));
  };
  const day = busy && busy.length ? new Date(isToday ? now : new Date(localMidnight(now).getTime() + 86400000)) : null;
  return (
    <div className="crm-td__strip" ref={ref} style={{ height: top + lanes * 96 + 12 }}>
      {Array.from({ length: hours }, (_, i) => (
        <div key={i} className="crm-td__hour" style={{ left: pos(range.start + i) }}>
          <span>{String(range.start + i).padStart(2, "0")}:00</span>
        </div>
      ))}
      {isToday && nowH >= range.start && nowH <= range.end && (
        <div className="crm-td__now" style={{ left: pos(nowH) }}>
          <span className="crm-td__now-lbl">now {hhmm(now)}</span>
        </div>
      )}
      {busyLaned.map(({ block, lane }) => {
        const s0 = clip(block.start, day);
        const s1 = clip(block.end, day);
        if (s1 <= s0) return null;
        return (
          <div
            key={block.id}
            className={`crm-td__busy${block.out_of_office ? " is-ooo" : ""}`}
            title={`${hhmm(block.start)}–${hhmm(block.end)} ${block.title}`}
            style={{ left: pos(s0), width: `calc(${pos(s1)} - ${pos(s0)})`, top: 24 + lane * (BUSY_H + 4), height: BUSY_H }}
          >
            <span className="crm-td__busy-t">{hhmm(block.start)}</span> {block.title}
          </div>
        );
      })}
      {!calls.length && !busyLaned.length && <div className="crm-td__nocalls">No calls. The day is yours.</div>}
      {laned.map(({ call, lane }) => (
        <CallCard
          key={call.event_id}
          call={call}
          next={call === next}
          past={new Date(call.at) < now}
          onOpen={onOpen}
          style={{
            // Right-aligned into the strip when it would run off the end.
            left: `min(${pos(hourOf(call.at))}, calc(100% - ${CARD_W}px))`,
            top: top + lane * 96,
            width: CARD_W,
          }}
        />
      ))}
    </div>
  );
}

function CallCard({ call, next, past, onOpen, style, list }) {
  const what = meetingPurpose(call.title, call.company_name);
  const cls = [
    "crm-td__call",
    list ? "crm-td__call--list" : "",
    next ? "is-next" : "",
    call.declined ? "is-declined" : "",
    past ? "is-past" : "",
  ].join(" ");
  const prep = call.contact_id ? (
    <button type="button" className="crm-td__prep" onClick={() => onOpen(call)}>prep ↗</button>
  ) : null;
  if (list) {
    return (
      <div className={cls}>
        <div className="crm-td__call-time">{hhmm(call.at)}</div>
        <div className="crm-td__call-body">
          <div className="crm-td__call-row"><b>{call.company_name}</b>{prep}</div>
          <div className="crm-td__call-who">{[what, callWho(call)].filter(Boolean).join(" · ")}</div>
          {call.declined && <Declined />}
        </div>
      </div>
    );
  }
  return (
    <div className={cls} style={style}>
      <div className="crm-td__call-row">
        <span className="crm-td__call-time">{hhmm(call.at)}</span>
        {what && <span className="crm-td__call-what">{what}</span>}
        {prep}
      </div>
      <div className="crm-td__call-co">{call.company_name}</div>
      <div className="crm-td__call-who">{callWho(call)}</div>
      {call.declined && <Declined />}
    </div>
  );
}

function Declined() {
  return (
    <div className="crm-td__declined">
      <span className="crm-td__pulse" aria-hidden="true" />They declined the invite
    </div>
  );
}

function MyDay({ calls, busy, invites, now, mobile, onOpen }) {
  const { today, tomorrow } = bucketCalls(calls, now);
  const blocks = bucketBusy(busyFor(busy, calls), now);
  const next = nextCall(today, now);
  // Busy time inside the day widens the strip like a call does; a block running
  // past midnight does not drag it to 24:00.
  const inDay = (b) => new Date(b.start).getDate() === new Date(b.end).getDate();
  const range = stripRange([
    ...today, ...tomorrow,
    ...[...blocks.today, ...blocks.tomorrow].filter(inDay).map((b) => ({ at: b.start, end_at: b.end })),
  ]);
  const tmr = new Date(localMidnight(now).getTime() + 86400000);
  const days = [
    { label: "Today", date: now, calls: today, busy: blocks.today, isToday: true },
    { label: "Tomorrow", date: tmr, calls: tomorrow, busy: blocks.tomorrow, isToday: false },
  ];
  const summary = `${today.length || "No"} call${today.length === 1 ? "" : "s"} today · ${tomorrow.length} tomorrow`;
  return (
    <div className="crm-card crm-td__day">
      <div className="crm-td__cardhead">
        <div className="crm-td__h3">My day</div>
        <div className="crm-td__muted">{summary}</div>
      </div>
      {days.map((d) => (
        <div key={d.label} className="crm-td__dayrow">
          <div className="crm-td__daylbl">
            <div className="crm-td__daylbl-t">{d.label}</div>
            <div className="crm-td__daylbl-d">
              {d.date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
            </div>
            {mobile && d.isToday && next && <div className="crm-td__daylbl-n">Next {untilLabel(next.at, now)}</div>}
          </div>
          {mobile ? (
            <div className="crm-td__daylist">
              {!d.calls.length && !d.busy.length && <div className="crm-td__muted">No calls. The day is yours.</div>}
              {/* Calls and busy time in one list, in time order. */}
              {[...d.calls.map((c) => ({ at: c.at, call: c })), ...d.busy.map((b) => ({ at: b.start, busy: b }))]
                .sort((x, y) => new Date(x.at) - new Date(y.at))
                .map(({ call: c, busy: b }) => (c ? (
                  <CallCard key={c.event_id} call={c} list next={c === next} past={new Date(c.at) < now} onOpen={onOpen} />
                ) : (
                  <div key={b.id} className={`crm-td__busyrow${b.out_of_office ? " is-ooo" : ""}`}>
                    <span>{hhmm(b.start)}–{hhmm(b.end)}</span> {b.title}
                  </div>
                )))}
            </div>
          ) : (
            <DayStrip calls={d.calls} busy={d.busy} isToday={d.isToday} now={now} next={next} range={range} onOpen={onOpen} />
          )}
        </div>
      ))}
      {invites.length > 0 && (
        <div className="crm-td__invites">
          {invites.map((a) => (
            <div key={a.company_id} className="crm-td__invite">
              <span className="crm-td__pulse" aria-hidden="true" />
              <span>
                <b>{a.company_name}</b> · Call scheduled, {a.detail === "they declined the invite" ? "but they declined the invite" : "nothing on the calendar"}
              </span>
              <button type="button" className="crm-td__alert-act" onClick={() => onOpen(a)}>Check ↗</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- the queue */

// A former lead's newsletter or event promo reads as a reply until he says otherwise.
const BLASTABLE = new Set(["reply_waiting"]);

/*
 * ACTION ITEMS FROM A CALL, ticked off where they are listed (2026-10-26). The row
 * keeps what was ticked here until the refetch drops it, and turns into its receipt
 * once the last one is done. A tick is his word that it went: no email clears these.
 */
function CallItems({ action, onAllDone, onTicked }) {
  const [ticked, setTicked] = useState({});
  const [error, setError] = useState(null);
  // The one just ticked, with its Undo under the list (a tick by accident, 2026-10-27).
  const [last, setLast] = useState(null);
  const items = (action.items || []).filter((i) => !ticked[i.key]);
  const tick = async (e, item) => {
    e.stopPropagation();
    setError(null);
    setTicked((t) => ({ ...t, [item.key]: true }));
    try {
      await api(tickPath(item), tickRequest(item, true));
      // The last one: the row becomes its receipt, which carries the Undo.
      if (items.length === 1) onAllDone({ ...action, ticked: [item] });
      else {
        setLast(item);
        onTicked();
      }
    } catch (err) {
      setTicked((t) => ({ ...t, [item.key]: false }));
      setError(err.message);
    }
  };
  const undo = async (e) => {
    e.stopPropagation();
    setError(null);
    try {
      await api(tickPath(last), tickRequest(last, false));
      setTicked((t) => ({ ...t, [last.key]: false }));
      setLast(null);
      onTicked();
    } catch (err) {
      setError(err.message);
    }
  };
  return (
    <div className="crm-td__items" onClick={(e) => e.stopPropagation()}>
      {byCall(items).map((g) => (
        <div key={g.activity_id} className="crm-td__itemgroup">
          <div className="crm-td__itemcall">Action items from the {shortDate(g.call_at)} call</div>
          {g.items.map((item) => (
            <label key={item.key} className="crm-td__item" title={item.quote ? `“${item.quote}”` : undefined}>
              <input type="checkbox" checked={false} onChange={(e) => tick(e, item)} onClick={(e) => e.stopPropagation()} />
              <span>{item.text}</span>
            </label>
          ))}
        </div>
      ))}
      {last && (
        <div className="crm-td__itemdone">
          <span className="crm-td__tick">✓</span>
          <s>{last.text}</s>
          <button type="button" className="crm-td__quiet" onClick={undo}>Undo</button>
        </div>
      )}
      {error && <div className="crm-td__err">{error}</div>}
    </div>
  );
}

function QueueRow({ action, primary, open, drafting, mobile, now, onOpen, onDraft, onSent, onCloseDraft, onNotReply, onTicked }) {
  // Speaking to them today: the row says when, and opens the record to prep from.
  const byCall = answeredByCall(action);
  const replyable = !byCall && REPLYABLE.has(action.kind) && !!action.contact_id;
  const cold = action.section === "chasing";
  const stage = action.pipeline_stage ? BOARD_STAGE_LABELS[action.pipeline_stage] : null;
  const act = (e) => {
    if (e) e.stopPropagation();
    if (replyable) onDraft(action);
    else onOpen(action);
  };
  const body = (
    <div
      className={`crm-td__row${cold ? " is-cold" : ""}${open ? " is-open" : ""}${byCall ? " is-bycall" : ""}`}
      onClick={() => onOpen(action)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(action); } }}
      role="button"
      tabIndex={0}
    >
      <div className="crm-td__who">
        <span className={`crm-td__dot${WAITING.has(action.kind) ? " is-waiting" : ""}`} aria-hidden="true" />
        <div className="crm-td__who-t">
          <div className="crm-td__co">
            <span>{action.company_name || action.company_domain || "(unnamed)"}</span>
            <SourcePill source={action.lead_source} />
          </div>
          <div className="crm-td__meta">
            {action.contact_name && <span>{action.contact_name}</span>}
            {action.contact_name && stage && <span>·</span>}
            {stage && <span className="crm-td__stage">{stage}</span>}
          </div>
        </div>
      </div>
      <div className="crm-td__detail">
        {byCall ? callTodayLine(action, now)
          : action.kind === CALL_ACTIONS && (action.items || []).length
            ? <CallItems action={action} onAllDone={onSent} onTicked={onTicked} />
            : action.detail}
      </div>
      <div className="crm-td__end">
        {BLASTABLE.has(action.kind) && onNotReply && (
          <button
            type="button"
            className="crm-td__quiet"
            title="It was a newsletter or blast. Hide it and stop counting it as contact."
            onClick={(e) => { e.stopPropagation(); onNotReply(action); }}
          >
            Not a reply
          </button>
        )}
        {!byCall && <div className={`crm-td__wait${cold ? " is-red" : ""}`}>{waitLabel(action)}</div>}
        <button type="button" className={`crm-td__btn${primary ? " is-primary" : ""}`} onClick={act}>
          {byCall ? "Prep" : action.verb}
        </button>
      </div>
    </div>
  );
  return (
    <div className="crm-td__rowwrap">
      {mobile && replyable ? <SwipeAct label={action.verb} onAct={() => onDraft(action)}>{body}</SwipeAct> : body}
      {drafting && (
        <div className="crm-td__draft">
          <ReplyPanel action={action} onClose={onCloseDraft} onSent={() => onSent(action)} />
        </div>
      )}
    </div>
  );
}

function Receipt({ action, onUndo, onUndoTick }) {
  return (
    <div className="crm-td__receipt">
      <span className="crm-td__tick">✓</span>
      <span>{action.blast ? `${action.company_name || "Their"} mail set aside as a blast, not a reply` : doneText(action)}</span>
      <span className="crm-td__muted">just now</span>
      {action.blast && onUndo && (
        <button type="button" className="crm-td__quiet" onClick={() => onUndo(action)}>Undo</button>
      )}
      {action.ticked && onUndoTick && (
        <button type="button" className="crm-td__quiet" onClick={() => onUndoTick(action)}>Undo</button>
      )}
    </div>
  );
}

// The group drawn above the whole page rather than in the queue: hot leads waiting
// on his answer (arrangeQueue). It still counts in the summary and the weight bar.
const PINNED = "waiting";

function Queue({ groups, approvals, breakdown, preview, handled, mobile, ui }) {
  const shown = groups.filter((g) => g.rows.length || g.done.length);
  const remaining = groups.reduce((n, g) => n + owed(g.rows).length, 0);
  const summary = shown.length
    ? `${remaining} open across ${shown.length} group${shown.length === 1 ? "" : "s"}${approvals ? `, plus ${approvals} approval${approvals === 1 ? "" : "s"}` : ""}`
    : approvals
      ? `${approvals} approval${approvals === 1 ? "" : "s"} waiting`
      : "Every group checked";
  return (
    <div className="crm-td__queue">
      <div className="crm-td__qhead">
        <div className="crm-td__h2">What needs you</div>
        <div className="crm-td__muted">{summary}</div>
      </div>

      {shown.length > 0 && (
        <div className="crm-td__weights">
          {shown.map((g) => (
            <div key={g.key} className="crm-td__seg" style={{ flexGrow: Math.max(owed(g.rows).length + g.done.length, 1) }}>
              <div className={`crm-td__seg-bar${owed(g.rows).length ? "" : " is-clear"}`}>{owed(g.rows).length || "✓"}</div>
              <div className="crm-td__seg-lbl">{SECTION_SHORT[g.key] || g.title}</div>
            </div>
          ))}
        </div>
      )}

      {!shown.length && (
        <div className="crm-card crm-td__qempty">
          <div className="crm-td__qempty-h">Queue’s empty. Every reply sent, every nudge nudged.</div>
          <div className="crm-td__qempty-p">
            {handled ? `${handled} handled today. ` : ""}Anything past its window waits on Follow-ups.
          </div>
          <div className="crm-td__qempty-acts">
            <Link className="crm-btn crm-btn--secondary" to="/">Open the board ↗</Link>
            <Link className="crm-btn crm-btn--secondary" to="/follow-ups">Follow-ups ↗</Link>
          </div>
        </div>
      )}

      {shown.filter((g) => g.key !== PINNED).map((g) => <QueueGroup key={g.key} g={g} mobile={mobile} ui={ui} />)}

      {approvals > 0 && (
        <div className="crm-td__appr">
          <div className="crm-td__appr-main">
            <div className="crm-td__appr-k">
              <span>Approvals</span>
              <span className="crm-td__appr-n">{approvals}</span>
            </div>
            <div className="crm-td__appr-t">
              {approvals} {approvals === 1 ? "change" : "changes"} from calls, email and PandaDoc
              {breakdown && breakdown.length ? `: ${breakdown.map((b) => b.text).join(", ")}` : ""}
            </div>
            {preview && preview.length > 0 && (
              <div className="crm-td__appr-list">
                {preview.map((p) => (
                  <div key={p.id}>
                    <b>{p.company_name || "A contact"}</b> {approvalChange(p)}
                  </div>
                ))}
                {approvals > preview.length && <div className="crm-td__appr-more">and {approvals - preview.length} more</div>}
              </div>
            )}
          </div>
          <Link className="crm-td__appr-btn" to="/approvals">Review all {approvals}</Link>
        </div>
      )}

    </div>
  );
}

function QueueGroup({ g, mobile, ui }) {
  return (
    <div className={`crm-card crm-td__group${g.key === "hot" || g.key === PINNED ? " is-hot" : ""}${g.key === PINNED ? " is-pinned" : ""}`}>
      <div className="crm-td__ghead">
        <div className="crm-td__gtitle">{g.title}</div>
        {owed(g.rows).length > 0
          ? <span className="crm-td__count">{owed(g.rows).length}</span>
          : !g.rows.length && <span className="crm-td__cleared">Cleared</span>}
        {SECTION_HINTS[g.key] && <div className="crm-td__muted crm-td__ghint">{SECTION_HINTS[g.key]}</div>}
      </div>
      {ui.flash === g.key && <div className="crm-td__flash">{g.title}: cleared. Nice.</div>}
      {g.done.map((a) => <Receipt key={`done-${rowKey(a)}`} action={a} onUndo={ui.onUndoNotReply} onUndoTick={ui.onUndoTick} />)}
      {g.rows.map((a) => (
        <QueueRow
          key={rowKey(a)}
          action={a}
          mobile={mobile}
          primary={ui.primary === rowKey(a)}
          now={ui.now}
          open={ui.peek && ui.peek.key === rowKey(a)}
          drafting={ui.draft === rowKey(a)}
          onOpen={ui.onOpen}
          onDraft={ui.onDraft}
          onSent={ui.onSent}
          onCloseDraft={ui.onCloseDraft}
          onNotReply={ui.onNotReply}
          onTicked={ui.onTicked}
        />
      ))}
      {mobile && g.rows.some((a) => REPLYABLE.has(a.kind) && a.contact_id) && (
        <div className="crm-td__swipehint">↳ swipe right to act, or tap the button</div>
      )}
    </div>
  );
}

// "→ Agreement + legal", "→ Not a fit". What the change would DO, in the board's words.
function approvalChange(p) {
  const to = p.to_value;
  if (p.change_type === "company_stage" && to) return `→ ${BOARD_STAGE_LABELS[to] || to}`;
  if (p.change_type === "company_status" && to) {
    const words = { client: "Closed won", lost: "Closed lost", recycled: "Parked", not_a_fit: "Not a fit", prospect: "Back in the pipeline" };
    return `→ ${words[to] || to}`;
  }
  if (p.change_type === "add_to_pipeline") return "→ New lead";
  if (p.change_type === "merge_company") return "→ Merge duplicate";
  if (p.change_type === "set_quote") return "→ New pricing";
  return "→ Update";
}

/* ------------------------------------------------------------ the rail */

function Pipeline({ pipeline, t, now }) {
  const rows = pipelineRows(pipeline);
  if (!rows.length) return null;
  const weekday = new Date(now.getTime() - 7 * 86400000).toLocaleDateString("en-US", { weekday: "short" });
  return (
    <div className="crm-card crm-td__pipe">
      <div className="crm-td__cardhead">
        <div className="crm-td__h3">Pipeline</div>
        <div className="crm-td__muted">{pipeline.open} open · vs last {weekday}</div>
        <Link className="crm-td__cardlink" to="/">Board ↗</Link>
      </div>
      <div className="crm-td__pipe-rows">
        {rows.map((r) => (
          <Link key={r.key} to="/" className={`crm-td__pipe-row${r.won ? " is-won" : ""}`}>
            <span className="crm-td__pipe-name">{r.name}</span>
            <span className="crm-td__pipe-track">
              {r.count > 0 && <span className="crm-td__pipe-bar" style={{ width: `${r.pct * t}%` }} />}
            </span>
            <span className="crm-td__pipe-n">{r.count}</span>
            <span className="crm-td__pipe-d">{r.delta}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

const FEED_SHOWN = 12;

function Feed({ feed, now, onChange }) {
  const [all, setAll] = useState(false);
  // Leads whose "replied" he set aside as a blast here: key -> the rows tagged, so
  // the receipt can undo exactly those. Kept until the page reloads.
  const [aside, setAside] = useState({});
  const leadKey = (e) => e.company_id || e.company_name;
  const notReply = async (e) => {
    try {
      const { activity_ids } = await api("/activities/not-a-reply", {
        method: "POST",
        body: JSON.stringify({ activity_ids: replyIds(e) }),
      });
      setAside((m) => ({ ...m, [leadKey(e)]: activity_ids }));
      onChange();
    } catch (err) {
      window.alert(`Could not set it aside: ${err.message}`);
    }
  };
  const undo = async (e) => {
    try {
      await api(`/companies/${e.company_id}/not-a-reply/undo`, {
        method: "POST",
        body: JSON.stringify({ activity_ids: aside[leadKey(e)] }),
      });
      setAside((m) => { const n = { ...m }; delete n[leadKey(e)]; return n; });
      onChange();
    } catch (err) {
      window.alert(`Could not undo: ${err.message}`);
    }
  };
  // One line per lead, under the day of its latest event (feedByLead).
  const leads = useMemo(() => feedByLead(feed), [feed]);
  const list = all ? leads : leads.slice(0, FEED_SHOWN);
  const groups = feedGroups(list, now);
  return (
    <div className="crm-card crm-td__feed">
      <div className="crm-td__h3">What happened</div>
      {!groups.length && <div className="crm-td__muted">A quiet week. Nothing moved.</div>}
      {groups.map((g) => (
        <div key={g.label} className="crm-td__fgroup">
          <div className="crm-td__flabel">{g.label}</div>
          {g.items.map((e, i) => {
            if (aside[leadKey(e)]) {
              return (
                <div key={i} className="crm-td__fitem">
                  <div className="crm-td__mark crm-td__mark--moved">✓</div>
                  <div className="crm-td__fitem-t">
                    <div className="crm-td__fitem-h is-quiet">{e.company_name}: set aside as a blast</div>
                  </div>
                  <button type="button" className="crm-td__quiet" onClick={() => undo(e)}>Undo</button>
                </div>
              );
            }
            const line = feedLeadLine(e);
            if (line.mark === "win") {
              return (
                <div key={i} className="crm-td__win">
                  <div className="crm-td__win-top"><span className="crm-td__win-tag">Won</span><span>{feedTime(e.at, now)}</span></div>
                  <div className="crm-td__win-t">{line.title}</div>
                  <div className="crm-td__win-s">{line.sub}</div>
                </div>
              );
            }
            return (
              <div key={i} className="crm-td__fitem">
                <div className={`crm-td__mark crm-td__mark--${line.mark}`}>{MARK_GLYPHS[line.mark]}</div>
                <div className="crm-td__fitem-t">
                  <div className={`crm-td__fitem-h${line.mark === "lost" ? " is-quiet" : ""}`}>{line.title}</div>
                  {line.sub && <div className="crm-td__fitem-s">{line.sub}</div>}
                </div>
                <div className="crm-td__fitem-end">
                  <div className="crm-td__fitem-time">{feedTime(e.at, now)}</div>
                  {e.company_id && replyIds(e).length > 0 && (
                    <button
                      type="button"
                      className="crm-td__quiet"
                      title="It was a newsletter or blast. Take it off the feed and stop counting it as contact."
                      onClick={() => notReply(e)}
                    >
                      Not a reply
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {leads.length > FEED_SHOWN && (
        <button type="button" className="crm-linkact crm-td__more" onClick={() => setAll((v) => !v)}>
          {all ? "Show less" : `Show all ${leads.length}`}
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- the page */

export default function TodayPage() {
  const mobile = useIsMobile();
  const now = useNow();
  const t = useIntro();
  const { refresh: refreshBadge } = useToday();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null); // rowKey of the row whose composer is open
  const [peek, setPeek] = useState(null); // { key, contactId, kind }
  const peekRef = useRef(null);
  peekRef.current = peek;
  // The open record's own guard (ContactDetailPage fills it in): a row switch or
  // an inline draft from out here would unmount the record, and with it a reply he
  // was writing inside it, so an edited draft there asks first. The record checks
  // its own DOM, which on a phone is a portal outside this page.
  const recordGuard = useRef(null);
  const leaveRecord = useCallback((proceed) => (recordGuard.current ? recordGuard.current(proceed) : proceed()), []);
  // Rows sent from this page, kept as receipts after the refetch drops them — the
  // server no longer lists a debt that has been paid, but the page should say so.
  const [sessionDone, setSessionDone] = useState([]);
  const [flash, setFlash] = useState(null);
  const flashTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const since = localMidnight().toISOString();
      setData(await api(`/today?brief=1&since=${encodeURIComponent(since)}`));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const model = useMemo(() => {
    if (!data) return null;
    const doneKeys = new Set(sessionDone.map(rowKey));
    const sections = data.sections
      .filter((s) => !NOT_QUEUE.has(s.key))
      .map((s) => ({
        key: s.key,
        title: s.title,
        rows: s.rows.filter((a) => !doneKeys.has(rowKey(a))).map((a) => ({ ...a, section: s.key })),
      }));
    // Hot leads waiting on him above everything, then the other hot leads, wake-ups
    // set aside: see arrangeQueue in todayModel.mjs.
    const arranged = arrangeQueue(sections, hotIds(data.hot, now));
    const withDone = (g) => ({ ...g, done: sessionDone.filter((a) => a.group === g.key) });
    const queue = [arranged.waiting, arranged.hot, ...arranged.must].map(withDone);
    const invites = (data.sections.find((s) => s.key === "invites") || { rows: [] }).rows;
    // A call with no invite is on the day card rather than in a queue group, but it
    // is still work: it counts, as it does in the nav badge.
    // Rows a call today answers are listed but not owed (answeredByCall).
    const remaining = queue.reduce((n, g) => n + owed(g.rows).length, 0) + invites.length;
    // Handled today = the server's count (mail sent, snoozes) plus anything sent from
    // here that the server has not seen yet, by company so one send is one item.
    const handledIds = new Set((data.handled || []).map((h) => h.company_id));
    for (const a of sessionDone) handledIds.add(a.company_id);
    const done = handledIds.size;
    const first = queue.flatMap((g) => owed(g.rows))[0] || null;
    const callsToday = bucketCalls(data.calls, now).today.length;
    const win = (data.feed || []).find((e) => e.kind === "won") || null;
    const pinned = queue[0];
    return { queue, pinned, invites, remaining, done, total: done + remaining, first, callsToday, win };
  }, [data, sessionDone, now]);

  // Opening the record leaves an open inline draft where it is: clicking the row
  // to read the history before replying is the normal order, and closing the
  // composer threw away whatever he had typed into it.
  const onOpen = useCallback((item) => {
    if (!item.contact_id) return;
    const next = { key: item.kind ? rowKey(item) : `call:${item.event_id}`, contactId: item.contact_id, kind: item.kind || null };
    const cur = peekRef.current;
    // The same person stays mounted (the record is keyed on the contact), so
    // nothing in it is lost; another person replaces it, and asks first.
    if (cur && String(cur.contactId) === String(next.contactId)) setPeek(next);
    else leaveRecord(() => setPeek(next));
  }, [leaveRecord]);
  const onDraft = useCallback((a) => {
    // The inline draft replaces the record beside it: one composer per email, and
    // an edited one in the record is asked about before it goes.
    const go = () => {
      setPeek(null);
      setDraft((k) => (k === rowKey(a) ? null : rowKey(a)));
    };
    if (peekRef.current) leaveRecord(go);
    else go();
  }, [leaveRecord]);
  const onSent = useCallback((a) => {
    setDraft(null);
    setSessionDone((list) => [...list.filter((x) => rowKey(x) !== rowKey(a)), a]);
    // Did that empty its group? Then the group says so, briefly.
    const group = model && model.queue.find((g) => g.key === a.group);
    if (group && group.rows.length === 1) {
      setFlash(a.group);
      clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlash(null), 2600);
    }
    refreshBadge();
    load();
  }, [model, load, refreshBadge]);

  const onNotReply = useCallback(async (a) => {
    try {
      const { activity_ids } = await api(`/companies/${a.company_id}/not-a-reply`, { method: "POST" });
      if (peek && peek.key === rowKey(a)) leaveRecord(() => setPeek(null));
      setDraft((k) => (k === rowKey(a) ? null : k));
      setSessionDone((list) => [...list.filter((x) => rowKey(x) !== rowKey(a)), { ...a, blast: activity_ids }]);
      refreshBadge();
      load();
    } catch (err) {
      window.alert(`Could not set it aside: ${err.message}`);
    }
  }, [peek, load, refreshBadge, leaveRecord]);
  const onUndoNotReply = useCallback(async (a) => {
    try {
      await api(`/companies/${a.company_id}/not-a-reply/undo`, {
        method: "POST",
        body: JSON.stringify({ activity_ids: a.blast }),
      });
      setSessionDone((list) => list.filter((x) => rowKey(x) !== rowKey(a)));
      refreshBadge();
      load();
    } catch (err) {
      window.alert(`Could not undo: ${err.message}`);
    }
  }, [load, refreshBadge]);

  // The receipt's Undo after the last of a call's action items was ticked: the
  // item goes back on, and the row with it.
  const onUndoTick = useCallback(async (a) => {
    try {
      await Promise.all(a.ticked.map((item) => api(tickPath(item), tickRequest(item, false))));
      setSessionDone((list) => list.filter((x) => rowKey(x) !== rowKey(a)));
      refreshBadge();
      load();
    } catch (err) {
      window.alert(`Could not undo: ${err.message}`);
    }
  }, [load, refreshBadge]);

  if (error) return <div className="crm-empty">Could not load Today: {error}</div>;
  if (!data || !model) return <div className="crm-empty">Working out what needs you…</div>;

  const allClear = model.remaining === 0 && !data.approvals;
  // The record opens beside the list, never as a navigation. Keyed on the contact so
  // switching rows remounts rather than showing the last person while the next loads.
  const record = peek ? (
    <ContactDetailPage
      key={peek.contactId}
      contactId={peek.contactId}
      embedded
      onClose={() => { setPeek(null); load(); }}
      // A send or a dismissed bar in the record changes the queue beside it.
      onChange={() => { refreshBadge(); load(); }}
      // The row's action goes on top of the record's Next with its composer open,
      // unless this row's own inline draft is open: one composer per email.
      openAction={draft === peek.key ? null : peek.kind}
      hostLabel="Today"
      guardRef={recordGuard}
    />
  ) : null;
  const ui = {
    primary: draft || peek ? null : model.first && rowKey(model.first),
    draft, peek, flash, now, onOpen, onDraft, onSent, onCloseDraft: () => setDraft(null),
    onNotReply, onUndoNotReply, onUndoTick,
    // One of a call's action items ticked, others left: the row stays, re-read.
    onTicked: () => { refreshBadge(); load(); },
  };

  return (
    <div className={`crm-td${mobile ? " crm-td--mobile" : ""}${peek && !mobile ? " crm-td--peek" : ""}`}>
      {/* Hot leads waiting on his answer come before everything else on the page,
          the greeting and the day included (Gabriel, 2026-10-15). */}
      {(model.pinned.rows.length > 0 || model.pinned.done.length > 0) && (
        <QueueGroup g={model.pinned} mobile={mobile} ui={ui} />
      )}

      {allClear ? (
        <AllClear now={now} calls={data.calls} win={model.win} />
      ) : (
        <Hero
          now={now}
          remaining={model.remaining}
          callsToday={model.callsToday}
          invites={model.invites}
          done={model.done}
          total={model.total}
          first={model.first}
          t={t}
          onOpen={onOpen}
          onRefresh={load}
        />
      )}

      <MyDay calls={data.calls || []} busy={data.busy || []} invites={model.invites} now={now} mobile={mobile} onOpen={onOpen} />

      <div className="crm-td__grid">
        <Queue
          groups={model.queue}
          approvals={data.approvals}
          breakdown={data.approvals_breakdown}
          preview={data.approvals_preview}
          handled={model.done}
          mobile={mobile}
          ui={ui}
        />
        <div className="crm-td__rail">
          {/* On a desktop the record takes the rail's place, as in the mockup, so the
              queue beside it stays whole and every row's button stays reachable. */}
          {peek && !mobile ? (
            <aside className="crm-peek crm-td__peek">{record}</aside>
          ) : (
            <>
              {!mobile && <Pipeline pipeline={data.pipeline} t={t} now={now} />}
              <Feed feed={data.feed || []} now={now} onChange={refreshBadge} />
            </>
          )}
        </div>
      </div>

      {data.backlog > 0 && (
        <div className="crm-td__foot">
          {data.backlog} older {data.backlog === 1 ? "item" : "items"} past {data.backlog === 1 ? "its" : "their"} window. Triage on{" "}
          <Link to="/follow-ups">Follow-ups ↗</Link>
        </div>
      )}

      {/* A phone has no rail: the record slides over the page, as on Leads. */}
      {peek && mobile && <aside className="crm-peek crm-leads__peek">{record}</aside>}
    </div>
  );
}
